import { LogoText } from '@aibees/shared/components/logo-text';
import { SwitchMode } from '@aibees/shared/components/settings/components/switch-mode';
import { SvgColor } from '@aibees/shared/components/svg-color';
import { Notifications } from '@aibees/shared/features/notifications';
import { ClearAllNotificationsAction } from '@aibees/shared/features/notifications/actions/clear-all-notifications-action';
import { useResponsive } from '@aibees/shared/hooks';
import { UpgradePlanButton } from '@aibees/shared/layouts/_common/components/upgrade-plan-button';
import { bgBlur } from '@aibees/shared/theme/css';
import { AppBar, Box, IconButton, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AccountPopover } from '../_common';
import { HEADER, LAYOUT } from '../config-layout';
import * as Styled from './styles';
import type { DashboardLayoutHeaderOptions } from './types';

type Props = {
  onOpenNav?: VoidFunction;
  options?: DashboardLayoutHeaderOptions;
  withOutZIndex?: boolean;
};

export default function Header({ onOpenNav, options, withOutZIndex }: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mdDown = useResponsive('down', 'md');

  const renderContent = (
    <Stack
      className="Header"
      flexGrow={1}
      direction="column"
      alignItems="center"
    >
      <Stack
        className="HeaderTop"
        flexGrow={1}
        direction="row"
        alignItems="center"
        spacing={{ xs: 0.5, sm: 1 }}
        width="100%"
        sx={{
          borderBottom: `1px solid ${
            isDark ? theme.palette.primary[100] : theme.palette.grey[300]
          }`,
          height: {
            lg: HEADER.TOP,
            md: HEADER.T_TABLET,
            xs: HEADER.T_MOBILE,
          },
          // Don't add padding here because it breaks background image view
        }}
      >
        {mdDown && !options?.hideNavVertical && (
          <IconButton onClick={onOpenNav} size="small">
            <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
          </IconButton>
        )}

        <Styled.LogoWrapper>
          <LogoText />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
            }}
          >
            {options?.breadcrumb && options?.breadcrumb}
          </Box>
        </Styled.LogoWrapper>

        <Styled.HeaderTopRightContainer>
          {options?.end}

          {!options?.hideNotification && (
            <Notifications
              actions={[
                <ClearAllNotificationsAction key="clear-all-notifications-action" />,
              ]}
            />
          )}
          {!options?.hideUpgradeButton && <UpgradePlanButton />}

          {options?.showThemeModeSwitch && <SwitchMode />}

          <AccountPopover {...options?.accountMenu} credit={options?.credit} />
        </Styled.HeaderTopRightContainer>
      </Stack>
      {options?.start && (
        <Stack
          className="HeaderBottom"
          flexGrow={1}
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 1 }}
          width="100%"
          sx={{
            borderBottom: `1px solid ${
              isDark ? theme.palette.grey[900] : theme.palette.grey[300]
            }`,
          }}
          px={1}
          py={1}
        >
          {options?.start}
        </Stack>
      )}
    </Stack>
  );
  return (
    <AppBar
      position="relative"
      sx={{
        maxHeight: {
          xs: HEADER.H_MOBILE,
          md: HEADER.H_TABLET,
          lg: HEADER.H_DESKTOP,
        },
        zIndex: withOutZIndex ? 0 : theme.zIndex.appBar + 1,
        borderRadius: `${HEADER.BORDER_RADIUS}px`,
        mx: `${LAYOUT.SPACE}px`,
        width: 'unset',
        ...bgBlur({
          color: isDark ? theme.palette.grey[800] : theme.palette.common.white,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(mdDown && {
          maxHeight: HEADER.H_DESKTOP,
        }),
      }}
    >
      <Styled.Toolbar sx={{ height: 1 }}>{renderContent}</Styled.Toolbar>
    </AppBar>
  );
}
